import { useMutation } from '@apollo/client';
import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Box from '@risk-smart/themed-cloudscape-components/box';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CatalogNode } from '@risksmart-app/domain/src/types/catalog';
import { ingestionConfigJsonSchema } from '@risksmart-app/domain/src/types/ingestion-filters';
import {
  InsertIngestionConfigDocument,
  UpdateIngestionConfigDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TabHeader from 'src/components/tab-header/TabHeader';
import { useGetCatalog } from 'src/hooks/queries/ingestion-config/useGetCatalog';
import { useGetIngestionConfigs } from 'src/hooks/queries/ingestion-config/useGetIngestionConfigs';

import { evictField } from '@/utils/graphqlUtils';

import {
  hydrateSelectedIds,
  mergeIngestionFilters,
  serializeSelection,
} from './catalog-selection';
import CatalogPicker from './CatalogPicker';

const setsEqual = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every((id) => b.has(id));

// Stable reference for the not-yet-loaded catalog. A `= []` default in the
// destructure would create a new array every render, recomputing the
// memoised selection and re-firing the resync effect (infinite loop).
const EMPTY_NODES: CatalogNode[] = [];

const Tab: FC = () => {
  const { t } = useTranslation('common', { keyPrefix: 'regulatoryFeed' });
  const { t: ct } = useTranslation('common');
  const { addNotification } = useNotifications();

  const { data: ingestionConfigData } = useGetIngestionConfigs({
    queryArgs: {},
  });
  const existing = ingestionConfigData?.ingestion_config?.[0];

  const {
    data: catalogData,
    isLoading: catalogLoading,
    isError: catalogError,
  } = useGetCatalog();
  const nodes = catalogData ?? EMPTY_NODES;

  const existingFilters = useMemo(() => {
    const parsed = ingestionConfigJsonSchema.safeParse(
      existing?.IngestionConfig ?? {}
    );

    return parsed.success ? parsed.data.filters : undefined;
  }, [existing?.IngestionConfig]);

  const initialSelectedIds = useMemo(
    () => hydrateSelectedIds(existingFilters, nodes),
    [existingFilters, nodes]
  );

  const [selectedIds, setSelectedIds] =
    useState<Set<string>>(initialSelectedIds);
  const [removedStaleIds, setRemovedStaleIds] = useState<Set<string>>(
    new Set()
  );
  const [saving, setSaving] = useState(false);

  // Resync when the underlying config/catalog data changes (load, post-save refetch).
  useEffect(() => {
    setSelectedIds(initialSelectedIds);
    setRemovedStaleIds(new Set());
  }, [initialSelectedIds]);

  const [insertIngestionConfig] = useMutation(InsertIngestionConfigDocument, {
    update: (cache) => evictField(cache, 'ingestion_config'),
  });
  const [updateIngestionConfig] = useMutation(UpdateIngestionConfigDocument, {
    update: (cache) => evictField(cache, 'ingestion_config'),
  });

  const knownIds = useMemo(
    () => new Set(nodes.map((n) => n.externalId)),
    [nodes]
  );

  const visibleStaleIds = useMemo(() => {
    const saved = [
      ...(existingFilters?.standards?.ids ?? []),
      ...(existingFilters?.chapters?.ids ?? []),
    ];

    return [...new Set(saved)].filter(
      (id) => !knownIds.has(id) && !removedStaleIds.has(id)
    );
  }, [existingFilters, knownIds, removedStaleIds]);

  const dirty =
    !setsEqual(selectedIds, initialSelectedIds) || removedStaleIds.size > 0;

  const saveChanges = async () => {
    setSaving(true);
    try {
      const picker = serializeSelection(selectedIds, nodes);

      // Preserve any saved-but-stale ids the user has not explicitly removed.
      const keptStaleStandards = (existingFilters?.standards?.ids ?? []).filter(
        (id) => !knownIds.has(id) && !removedStaleIds.has(id)
      );
      const keptStaleChapters = (existingFilters?.chapters?.ids ?? []).filter(
        (id) => !knownIds.has(id) && !removedStaleIds.has(id)
      );

      const standardIds = [
        ...(picker.standards?.ids ?? []),
        ...keptStaleStandards,
      ];
      const chapterIds = [
        ...(picker.chapters?.ids ?? []),
        ...keptStaleChapters,
      ];

      const merged = mergeIngestionFilters(existingFilters, {
        standards: standardIds.length
          ? { mode: 'include', ids: standardIds }
          : undefined,
        chapters: chapterIds.length
          ? { mode: 'include', ids: chapterIds }
          : undefined,
      });

      const existingDoc =
        (existing?.IngestionConfig as Record<string, unknown> | undefined) ??
        {};
      const newConfig = { ...existingDoc, filters: merged };

      // The Ascent API key is managed in the internal Modules tab, so it is
      // intentionally not sent here — omitting ApiKey leaves the stored secret
      // untouched.
      if (existing) {
        await updateIngestionConfig({
          variables: {
            object: {
              Id: existing.Id,
              IngestionConfig: newConfig,
              OriginalTimestamp: existing.ModifiedAtTimestamp,
            },
          },
        });
      } else {
        await insertIngestionConfig({
          variables: {
            object: {
              IngestionConfig: newConfig,
            },
          },
        });
      }

      setRemovedStaleIds(new Set());
      addNotification({ type: 'success', content: t('save_success') });
    } catch {
      addNotification({ type: 'error', content: t('save_error') });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setSelectedIds(initialSelectedIds);
    setRemovedStaleIds(new Set());
  };

  return (
    <div>
      <TabHeader className={'py-6'}>{t('tab_title')}</TabHeader>

      <SpaceBetween size={'l'} direction={'vertical'}>
        <Box variant={'p'} color={'text-body-secondary'}>
          {t('page_help')}
        </Box>

        {dirty && <Alert type={'info'}>{t('unsaved_changes')}</Alert>}

        <div>
          <Box variant={'h3'}>{t('imported_heading')}</Box>
          <CatalogPicker
            nodes={nodes}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            staleIds={visibleStaleIds}
            onRemoveStaleId={(id) =>
              setRemovedStaleIds((prev) => new Set(prev).add(id))
            }
            loading={catalogLoading}
            error={catalogError}
          />
        </div>

        <div
          className={
            'gap-2 border-0 border-grey150 border-solid border-t-[0.5px] pt-4'
          }
        >
          <SpaceBetween direction={'horizontal'} size={'s'}>
            <Button
              variant={'primary'}
              onClick={saveChanges}
              loading={saving}
              disabled={!dirty}
            >
              {ct('save')}
            </Button>
            <Button onClick={cancel} disabled={!dirty}>
              {ct('cancel')}
            </Button>
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </div>
  );
};

export default Tab;
